'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiFetch } from './apiFetch';
import { azureAuthService } from '../services/azureAuthService';
import { AzureUser, AzureCallbackResponse } from '../types';

interface User {
  id: number | string;
  username: string;
  email: string;
  full_name?: string;
  is_admin: boolean;
  created_at?: string;
  last_login?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  authMethod: 'jwt' | 'microsoft_sso' | 'azure_sso' | null;
  login: (username: string, password: string) => Promise<boolean>;
  loginWithMicrosoft: () => Promise<void>;
  loginWithAzure: () => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authMethod, setAuthMethod] = useState<'jwt' | 'microsoft_sso' | 'azure_sso' | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const isAuthenticated = user !== null;

  // Check authentication status - extracted to be reusable
  const checkAuth = useCallback(async () => {
    try {
      // First check Azure SSO authentication
      const azureToken = localStorage.getItem('azureAuthToken');
      
      if (azureToken) {
        try {
          const isValid = await azureAuthService.verifyToken();
          
          if (isValid) {
            const userData = await azureAuthService.getCurrentUser();
            setUser(userData);
            setAuthMethod('azure_sso');
            setIsLoading(false);
            return;
          } else {
            // Azure token invalid, remove it
            azureAuthService.clearStoredToken();
          }
        } catch (error) {
          console.error('Azure token verification failed:', error);
          azureAuthService.clearStoredToken();
        }
      }

      // Check JWT authentication
      const jwtToken = localStorage.getItem('authToken');
      
      if (jwtToken) {
        try {
          const response = await apiFetch(`${BACKEND_URL}/api/auth/verify`);

          if (response.ok) {
            const userResponse = await apiFetch(`${BACKEND_URL}/api/auth/me`);

            if (userResponse.ok) {
              const userData = await userResponse.json();
              setUser(userData);
              setAuthMethod('jwt');
              setIsLoading(false);
              return;
            } else {
            }
          } else {
          }
          
          // JWT token invalid, remove it
          localStorage.removeItem('authToken');
        } catch (error) {
          localStorage.removeItem('authToken');
        }
      }

      // Check JWT authentication via cookies (fallback)
      try {
        const cookieResponse = await apiFetch(`${BACKEND_URL}/api/auth/cookie-status`);

        if (cookieResponse.ok) {
          const cookieData = await cookieResponse.json();
          
          if (cookieData.authenticated && cookieData.user) {
            setUser(cookieData.user);
            setAuthMethod('jwt');
            setIsLoading(false);
            
            // Sync localStorage with cookie for consistency
            if (!localStorage.getItem('authToken')) {
              // Note: We can't extract the token from the HTTP-only cookie,
              // but we know the user is authenticated via cookie
              console.log('🔄 JWT authentication restored from cookie');
            }
            return;
          } else {
          }
        } else {
        }
      } catch (error) {
        console.error('❌ Cookie auth check error:', error);
      }

      // Check Microsoft SSO authentication
      try {
        const ssoResponse = await apiFetch(`${BACKEND_URL}/api/auth/microsoft/status`);

        if (ssoResponse.ok) {
          const ssoData = await ssoResponse.json();
          
          if (ssoData.authenticated && ssoData.user) {
            setUser(ssoData.user);
            setAuthMethod('microsoft_sso');
            setIsLoading(false);
            return;
          } else {
          }
        } else {
        }
      } catch (error) {
      }

      // No valid authentication found
      setUser(null);
      setAuthMethod(null);
    } catch (error) {
      console.error('Auth check error:', error);
      setUser(null);
      setAuthMethod(null);
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  // Handle Azure callback from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');
    
    if (code) {
      // Handle Azure SSO callback
      const handleAzureCallback = async () => {
        try {
          setIsLoading(true);
          
          const response = await azureAuthService.handleAzureCallback(code, state || undefined);
          
          // Store the token
          azureAuthService.storeToken(response.access_token);
          
          // Get user data after storing token
          const userData = await azureAuthService.getCurrentUser();
          
          // Set user data
          setUser(userData);
          setAuthMethod('azure_sso');
          
          // Clear URL parameters
          window.history.replaceState({}, document.title, '/login');
          
          // Navigate to the page user came from or home page
          const from = location.state?.from?.pathname || '/';
          navigate(from, { replace: true });
        } catch (error) {
          console.error('Azure callback error:', error);
          // You might want to show an error message to the user
        } finally {
          setIsLoading(false);
        }
      };
      
      handleAzureCallback();
    } else if (error) {
      console.error('Azure SSO error:', error);
      // You might want to show an error message to the user
    }
  }, [location.search, navigate, location.state]);

  // Initial auth check
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Listen for storage changes (when token is added/removed in another tab)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'authToken' || e.key === 'azureAuthToken') {
        if (e.newValue) {
          // Token was added, recheck auth
          checkAuth();
        } else {
          // Token was removed, logout
          setUser(null);
          setAuthMethod(null);
          navigate('/login', { replace: true });
        }
      }
    };

    // Handle page visibility changes (when user returns to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden && !isAuthenticated && (localStorage.getItem('authToken') || localStorage.getItem('azureAuthToken'))) {
        // User returned to tab and has token but not authenticated, recheck
        checkAuth();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [navigate, isAuthenticated, checkAuth]);

  // Redirect to login if not authenticated and not on login page
  useEffect(() => {
    if (!isLoading && !isAuthenticated && location.pathname !== '/login') {
      navigate('/login', { state: { from: location } });
    }
  }, [isLoading, isAuthenticated, location.pathname, navigate, location]);

  const login = useCallback(async (login: string, password: string): Promise<boolean> => {
    try {
      const response = await apiFetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ login, password }),
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.access_token) {
          localStorage.setItem('authToken', data.access_token);
        }
        
        // Fetch user data after successful login
        const userResponse = await apiFetch(`${BACKEND_URL}/api/auth/me`);
        if (userResponse.ok) {
          const userData = await userResponse.json();
          console.log('🔐 Login successful, user data:', userData);
          setUser(userData);
          setAuthMethod('jwt');
          setIsLoading(false);
          return true;
        } else {
          console.error('Failed to fetch user data after login');
          return false;
        }
      } else {
        const errorData = await response.json();
        console.error('Login failed:', errorData);
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  }, []);

  const loginWithMicrosoft = useCallback(async () => {
    try {
      const response = await apiFetch(`${BACKEND_URL}/api/auth/microsoft/login`);
      
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.auth_url;
      } else {
        throw new Error('Failed to get Microsoft login URL');
      }
    } catch (error) {
      console.error('Microsoft login error:', error);
      throw error;
    }
  }, []);

  const loginWithAzure = useCallback(async () => {
    try {
      await azureAuthService.initiateAzureLogin();
    } catch (error) {
      console.error('Azure login error:', error);
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      // Call appropriate logout endpoint based on auth method
      if (authMethod === 'azure_sso') {
        await azureAuthService.logout();
      } else {
        await apiFetch(`${BACKEND_URL}/api/auth/logout`, {
          method: 'POST',
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local state regardless of backend response
      localStorage.removeItem('authToken');
      azureAuthService.clearStoredToken();
      setUser(null);
      setAuthMethod(null);
      navigate('/login', { replace: true });
    }
  }, [authMethod, navigate]);

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    authMethod,
    login,
    loginWithMicrosoft,
    loginWithAzure,
    logout,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 